// Category Leaders — fetch and structure the top-5 free and top-5 paid apps
// in the same App Store category for a given country.
//
// This module is pure domain logic: data shaping and error handling only.
// Network access lives in src/api/rssCharts.js.

import { fetchCategoryChart } from '../api/rssCharts.js';

const TOP_N = 5;

/**
 * @typedef {object} CategoryLeader
 * @property {number} rank - 1-based chart position.
 * @property {number} trackId - Apple app ID.
 * @property {string|null} name - App name as the RSS feed reports it.
 * @property {'free'|'paid'} chartType
 */

/**
 * @typedef {object} CategoryLeadersResult
 * @property {CategoryLeader[]} free - Top N free apps, sorted by rank.
 * @property {CategoryLeader[]} paid - Top N paid apps, sorted by rank.
 * @property {boolean} freeOk - Whether the free chart was read successfully.
 * @property {boolean} paidOk - Whether the paid chart was read successfully.
 */

/**
 * Fetch top-N free and paid category leaders for a country + genreId.
 * Never rejects — failures are folded into the result (empty array + *Ok = false).
 *
 * @param {string} country - Two-letter country code (e.g. 'us', 'jp').
 * @param {string} genreId - Apple genre ID (e.g. '6014' for Games).
 * @param {number} [topN=5]
 * @returns {Promise<CategoryLeadersResult>}
 */
export async function fetchCategoryLeaders(country, genreId, topN = TOP_N) {
  const [free, paid] = await Promise.allSettled([
    fetchCategoryChart(country, 'top-free', genreId, topN),
    fetchCategoryChart(country, 'top-paid', genreId, topN),
  ]);

  return {
    free: free.status === 'fulfilled'
      ? free.value.map((e) => ({ ...e, chartType: 'free' }))
      : [],
    paid: paid.status === 'fulfilled'
      ? paid.value.map((e) => ({ ...e, chartType: 'paid' }))
      : [],
    freeOk: free.status === 'fulfilled',
    paidOk: paid.status === 'fulfilled',
  };
}

/**
 * Returns true when neither the free nor paid chart loaded — almost always
 * a CORS failure on the Apple RSS feed (no proxy configured).
 *
 * Distinguishes the "feed unreachable" case from "category genuinely empty"
 * (which would show freeOk/paidOk true but both arrays empty).
 *
 * @param {CategoryLeadersResult} result
 * @returns {boolean}
 */
export function isFeedBlocked(result) {
  return !result.freeOk && !result.paidOk;
}

/**
 * Format a CategoryLeadersResult into a flat ranked list for display.
 * Each section (free, paid) is returned separately so the UI can render them
 * under distinct headings.
 *
 * @param {CategoryLeadersResult} result
 * @returns {{ free: CategoryLeader[], paid: CategoryLeader[] }}
 */
export function formatLeaderSections(result) {
  return {
    free: result.free.slice(0, TOP_N),
    paid: result.paid.slice(0, TOP_N),
  };
}
